import type { ReactElement } from "react";

import { Locked } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox, Tooltip } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { translate } from "app/filters/i18nFilter";
import { DropdownOptionText } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/components/models-selector/ModelSelector.styled";
import { AI_MODELS_CONFIG } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import type { ICountryObject } from "app/services/CountryService";
import { isNoTouch } from "app/UtilitiesAndConstants/UtilityFunctions/usersUtils";

import type { ExplicitLLModel } from "../../types";

import { ModelIcon } from "./ModelIcon";

interface IModelSelectorItemProps {
    model: ExplicitLLModel;
    isDisabled: boolean;
    isSelected: boolean;
    onClick: () => void;
    onLockedClick: () => void;
    selectedCountry?: ICountryObject;
}

export const ModelSelectorItem = ({
    model,
    isDisabled,
    isSelected,
    onClick,
    onLockedClick,
    selectedCountry,
}: IModelSelectorItemProps) => {
    const isNTUser = isNoTouch(swSettings);
    const isLocked = isNTUser && isDisabled;
    const label = translate(AI_MODELS_CONFIG[model].displayNameKey);

    const renderOptionLabel = (): ReactElement => (
        <Flex
            gap="8px"
            alignItems="center"
        >
            <ModelIcon model={model} />
            <DropdownOptionText
                fontVariant="input2"
                color={colorsPalettes.carbon[400]}
            >
                {label}
            </DropdownOptionText>
        </Flex>
    );

    if (isLocked) {
        return (
            <Combobox.Option
                id={model}
                textValue={label}
                onClick={onLockedClick}
                data-automation={`llm-setup-models-item-${model}-locked`}
            >
                {renderOptionLabel()}
                <Locked
                    size="xs"
                    color={colorsPalettes.carbon[200]}
                />
            </Combobox.Option>
        );
    }

    return (
        <Combobox.Option
            id={model}
            textValue={label}
            isDisabled={isDisabled}
            onClick={onClick}
            data-automation={`llm-setup-models-item-${model}`}
        >
            <Tooltip
                placement="left"
                enabled={isDisabled && !!selectedCountry}
                content={translate("llm.setup.models.disabled-tooltip", {
                    country: selectedCountry?.text ?? "",
                })}
            >
                <Flex
                    flex="1"
                    alignItems="center"
                    justifyContent="space-between"
                >
                    {renderOptionLabel()}
                    <Combobox.OptionCheckbox
                        isChecked={isSelected}
                        isDisabled={isDisabled}
                    />
                </Flex>
            </Tooltip>
        </Combobox.Option>
    );
};
